
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { TicketType } from './types';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import TicketForm from './tickets/TicketForm';
import TicketList from './tickets/TicketList';
import EmptyTicketState from './tickets/EmptyTicketState';

interface TicketsAdminProps {
  conferenceId?: string;
}

const initialTicketState: TicketType = {
  conference_id: null,
  name: '',
  description: '',
  price: 0,
  currency: 'USD',
  available_from: new Date().toISOString().split('T')[0],
  available_until: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
  quantity: null,
  sold: 0,
  active: true,
  features: ''
};

const TicketsAdmin: React.FC<TicketsAdminProps> = ({ conferenceId }) => {
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [editingTicket, setEditingTicket] = useState<TicketType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('ticket_types')
          .select('*')
          .order('price')
          .eq('conference_id', conferenceId || null);

        if (error) throw error;
        setTickets(data || []);
      } catch (error) {
        console.error('Error fetching ticket types:', error);
        toast({
          title: "Error",
          description: "Failed to load ticket data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [conferenceId, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!editingTicket) return;

    const { name, value, type } = e.target;
    
    if (type === 'number') {
      setEditingTicket({
        ...editingTicket,
        [name]: value === '' ? null : Number(value)
      });
    } else {
      setEditingTicket({
        ...editingTicket,
        [name]: value
      });
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    if (!editingTicket) return;
    
    setEditingTicket({
      ...editingTicket,
      active: checked
    });
  };

  const startAddingTicket = () => {
    setEditingTicket({
      ...initialTicketState,
      conference_id: conferenceId || null
    });
    setIsEditing(true);
  };

  const startEditingTicket = (ticket: TicketType) => {
    setEditingTicket({...ticket});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setEditingTicket(null);
    setIsEditing(false);
  };

  const saveTicket = async () => {
    if (!editingTicket) return;
    
    setSaving(true);
    try {
      if (editingTicket.id) {
        const { error } = await supabase
          .from('ticket_types')
          .update({
            name: editingTicket.name,
            description: editingTicket.description,
            price: editingTicket.price,
            currency: editingTicket.currency,
            available_from: editingTicket.available_from,
            available_until: editingTicket.available_until,
            quantity: editingTicket.quantity,
            active: editingTicket.active,
            features: editingTicket.features,
            conference_id: conferenceId || null
          })
          .eq('id', editingTicket.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('ticket_types')
          .insert([{
            name: editingTicket.name,
            description: editingTicket.description,
            price: editingTicket.price,
            currency: editingTicket.currency,
            available_from: editingTicket.available_from,
            available_until: editingTicket.available_until,
            quantity: editingTicket.quantity,
            active: editingTicket.active,
            features: editingTicket.features,
            conference_id: conferenceId || null
          }]);

        if (error) throw error;
      }

      const { data, error } = await supabase
        .from('ticket_types')
        .select('*')
        .eq('conference_id', conferenceId || null)
        .order('price');

      if (error) throw error;
      setTickets(data || []);
      
      setEditingTicket(null);
      setIsEditing(false);
      
      toast({
        title: "Success",
        description: editingTicket.id ? "Ticket updated successfully" : "Ticket created successfully",
      });
    } catch (error) {
      console.error('Error saving ticket:', error);
      toast({
        title: "Error",
        description: "Failed to save ticket data",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async (id: string) => {
    if (!confirm('Are you sure you want to delete this ticket type?')) return;

    try {
      const { error } = await supabase
        .from('ticket_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTickets(tickets.filter(ticket => ticket.id !== id));
      
      toast({
        title: "Success",
        description: "Ticket type deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast({
        title: "Error",
        description: "Failed to delete ticket type",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mb-4"></div>
        <p className="text-white">Loading tickets information...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Ticket Management</h2>
        {!isEditing && (
          <Button 
            onClick={startAddingTicket}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Ticket Type
          </Button>
        )}
      </div>

      {isEditing && editingTicket && (
        <TicketForm
          editingTicket={editingTicket}
          onSave={saveTicket}
          onCancel={cancelEditing}
          onInputChange={handleInputChange}
          onSwitchChange={handleSwitchChange}
          saving={saving}
        />
      )}

      {tickets.length > 0 ? (
        <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardContent className="p-0">
            <TicketList
              tickets={tickets}
              onEdit={startEditingTicket}
              onDelete={deleteTicket}
            />
          </CardContent>
        </Card>
      ) : (
        <EmptyTicketState onAdd={startAddingTicket} />
      )}
    </div>
  );
};

export default TicketsAdmin;
